// src/pages/markets/board/card/body/NewBlock.tsx
//
// The New column answers "is this worth being early on", not "what is it worth".
// Priority: how new → who is already in → what is on offer → how long the window
// is. Probability is deliberately demoted to the ladder's bars: on a market this
// young it is one person's opinion, and a headline number lies about it.
//
// One hue, one meaning: amber = freshness, green = tracked money is in,
// red = time pressure, grey = a zero.
//
// The ladder underneath is the same one every other column uses, so it now
// carries a capital column too. Here it is built from *every* group's scores,
// not the column's — "someone tracked is already in" is the New column's whole
// question, and which watchlist they belong to is a detail the pill above
// already names. Where two groups sit on one market, the larger position wins
// the cell; the ladder has one slot and the bigger number is the one that
// would make a reader stop.

import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { BoardScore, ScreenerEvent } from '../../api/boardTypes';
import { dominantCapital } from '../../api/metricEval';
import { BLOCK_GAP, BODY_INNER_HEIGHT, META_MARKET_BLOCK_HEIGHT } from '../cardLayout';
import {
    EM_DASH,
    PAST_END,
    ageTier,
    formatAge,
    formatCreatedDate,
    formatTimeLeft,
    formatUsd,
} from '../cardFormat';
import { useBoardGroups } from '../../state/boardGroupsContext';
import { MarketBlock } from './MarketBlock';
import { Figure } from '../../../../../components/common/page/Figure';
import { CardTail } from '../CardFigures';

const CLOSING_SOON_MS = 48 * 3_600_000;

// One score per market: the biggest position, whichever group holds it.
function bestScoreByMarket(scores: BoardScore[]): Map<string, BoardScore> {
    const out = new Map<string, BoardScore>();
    for (const s of scores) {
        const prev = out.get(s.market_id);
        if (!prev || (dominantCapital(s) ?? 0) > (dominantCapital(prev) ?? 0)) {
            out.set(s.market_id, s);
        }
    }
    return out;
}

export interface NewBlockProps {
    event: ScreenerEvent;
    // Every score of this event across groups: "is anyone tracked already in"
    // must not depend on which group the column happens to own.
    scores?: BoardScore[];
    activeTags?: readonly string[] | null;
    accentColor?: string;
    // Selection is live here too — the ladder is the card's only navigation
    // now, and pinning a row survives a re-render even without a conviction
    // row to read it.
    selectedMarketId?: string | null;
    onSelectMarket?: (marketId: string) => void;
    // Geometry comes from the caller so this block stops riding on constants
    // that belong to the conviction layout.
    marketBlockHeight?: number;
    now: number;
}

export function NewBlock({
    event,
    scores = [],
    accentColor,
    selectedMarketId = null,
    onSelectMarket,
    marketBlockHeight = META_MARKET_BLOCK_HEIGHT,
    now,
}: NewBlockProps) {
    const theme = useTheme();
    const boardGroups = useBoardGroups();
    const { warning, success, error, text } = theme.palette;

    const tier = ageTier(event.created_at, now);
    const ageColor =
        tier === 'hot'
            ? warning.main
            : tier === 'warm'
                ? alpha(warning.main, 0.78)
                : tier === 'today'
                    ? text.primary
                    : text.secondary;

    // The reason to read this card at all.
    const signals = event.open_signal_count ?? 0;
    const scoredGroups = [...new Set(scores.map(s => s.group))];
    const tracked = signals > 0 || scoredGroups.length > 0;

    const volume = event.volume ?? 0;
    const dead = volume <= 0;

    const endsAt = event.end_date ? Date.parse(event.end_date) : NaN;
    // Past end_date is not closing soon: open markets trade past the estimate.
    const closingSoon = Number.isFinite(endsAt) && endsAt > now && endsAt - now < CLOSING_SOON_MS;
    const resolved =
        event.resolution_status === 'resolved' || event.resolution_status === 'closed';

    // Not in the type sketch yet — read defensively so a missing backend field
    // is a silent no-pill rather than a compile error.
    const recurrence = (event as { recurrence?: string | null }).recurrence ?? null;

    const scoreByMarket = bestScoreByMarket(scores);

    const handleSelect = onSelectMarket ?? (() => { });

    const timeLeft = formatTimeLeft(event.end_date, now);
    const pastEnd = timeLeft === PAST_END;

    const groupNames = scoredGroups.map(boardGroups.titleOf);
    const trackedValue =
        groupNames.length === 1
            ? groupNames[0]
            : groupNames.length > 1
                ? `${groupNames.length} groups`
                : tracked
                    ? `${signals} signal${signals === 1 ? '' : 's'}`
                    : 'untouched';
    const trackedSub =
        groupNames.length > 1
            ? groupNames.join(' + ')
            : groupNames.length === 1
                ? 'has a position'
                : tracked
                    ? 'open'
                    : 'no tracked money';

    return (
        <Box
            style={{ height: BODY_INNER_HEIGHT }}
            sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: `${BLOCK_GAP}px`,
            }}
        >
            {/* The slate itself — every outcome, probability as a muted bar,
                tracked capital on the right where the other columns keep it. */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <MarketBlock
                    event={event}
                    selectedMarketId={selectedMarketId}
                    onSelectMarket={handleSelect}
                    height={marketBlockHeight}
                    scoreByMarket={scoreByMarket}
                    accentColor={accentColor}
                />
            </Box>

            <CardTail columns={4}>
                <Figure
                    dense
                    label="Listed"
                    value={formatAge(event.created_at, now)}
                    color={ageColor}
                    sub={formatCreatedDate(event.created_at, now)}
                />
                <Figure
                    dense
                    label="Traded"
                    value={dead ? EM_DASH : formatUsd(volume)}
                    color={dead ? text.disabled : undefined}
                    sub={
                        dead
                            ? 'no trades yet'
                            : event.volume_24h
                                ? `24h ${formatUsd(event.volume_24h)}`
                                : undefined
                    }
                />
                <Figure
                    dense
                    label={resolved ? 'Status' : 'Closes'}
                    value={resolved ? event.resolution_status : pastEnd ? 'past end' : timeLeft}
                    color={resolved || closingSoon ? error.main : undefined}
                    sub={recurrence ?? (pastEnd && !resolved ? 'estimated end' : undefined)}
                />
                <Figure
                    dense
                    label="Tracked"
                    value={trackedValue}
                    color={tracked ? success.main : text.disabled}
                    sub={trackedSub}
                    title={groupNames.join(' + ') || undefined}
                />
            </CardTail>
        </Box>
    );
}
